import { useEffect, useRef, useState } from 'react'
import { getCardTimeline } from '../services/api'
import {
  buildChainOfCustodySteps,
  normalizeApiTimelinePayload,
} from '../utils/ownerTimelineBuilder'
import { getOwnerCardDisplayName } from '../utils/ownerCardDisplay'

const PHASES = [
  { min: 0, title: 'INITIALIZING', sub: 'Connecting to services...' },
  { min: 20, title: 'READING CARD', sub: 'Fetching card data...' },
  { min: 45, title: 'VERIFYING NFT', sub: 'Checking records...' },
  { min: 70, title: 'LOADING HISTORY', sub: 'Building chain of custody...' },
  { min: 90, title: 'FINALIZING', sub: 'Validating timeline...' },
  { min: 100, title: 'COMPLETE', sub: 'All records loaded ✓' },
]

function getPhase(progress) {
  let p = PHASES[0]
  for (const ph of PHASES) {
    if (progress >= ph.min) p = ph
  }
  return p
}

function loadTimeline(card, order) {
  const fallback = () => buildChainOfCustodySteps(card, order)
  const fetchWithTimeout = async () => {
    try {
      const res = await getCardTimeline(card.id)
      const raw = res.data?.data ?? res.data
      const normalized = normalizeApiTimelinePayload(raw)
      if (normalized?.length) return normalized
    } catch {
      /* optional endpoint */
    }
    return fallback()
  }
  return Promise.race([
    fetchWithTimeout(),
    new Promise((resolve) => setTimeout(() => resolve(fallback()), 6000)),
  ])
}

export default function CardScanner({ isOpen, card, order, imageUrl, onComplete, onCancel }) {
  const canvasRef = useRef(null)
  const particleRafRef = useRef(null)
  const [progress, setProgress] = useState(0)
  const [timelineResult, setTimelineResult] = useState(null)
  const completedRef = useRef(false)
  const [imgError, setImgError] = useState(false)
  const reduceMotion =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches

  useEffect(() => {
    if (!isOpen) {
      setProgress(0)
      setTimelineResult(null)
      completedRef.current = false
      setImgError(false)
      return
    }
    completedRef.current = false
    setProgress(0)
    setTimelineResult(null)
    setImgError(false)

    loadTimeline(card, order).then(setTimelineResult)

    const start = Date.now()
    const id = setInterval(() => {
      const elapsed = Date.now() - start
      setProgress(Math.min(100, (elapsed / 3000) * 100))
    }, 50)
    return () => clearInterval(id)
  }, [isOpen, card?.id, order?.id])

  useEffect(() => {
    if (!isOpen || progress < 100 || !timelineResult || completedRef.current) return
    completedRef.current = true
    const t = setTimeout(() => onComplete?.(timelineResult), 400)
    return () => clearTimeout(t)
  }, [isOpen, progress, timelineResult, onComplete])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!isOpen || !canvas || reduceMotion) return

    const ctx = canvas.getContext('2d')
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    let w = 0
    let h = 0
    const particles = Array.from({ length: 60 }, () => ({
      x: Math.random(),
      y: Math.random(),
      s: 0.3 + Math.random() * 1.2,
      vy: 0.4 + Math.random() * 1.2,
      hue: Math.random() > 0.5 ? '#B49CF5' : '#8FD8E6',
    }))

    let active = true

    const resize = () => {
      const rect = canvas.getBoundingClientRect()
      w = rect.width
      h = rect.height
      canvas.width = w * dpr
      canvas.height = h * dpr
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    resize()
    window.addEventListener('resize', resize)

    const loop = () => {
      if (!active || !ctx || w === 0) return
      ctx.clearRect(0, 0, w, h)
      for (const pt of particles) {
        pt.y -= pt.vy * 0.004
        if (pt.y < 0) pt.y = 1
        ctx.beginPath()
        ctx.fillStyle = pt.hue
        ctx.globalAlpha = 0.35 + Math.random() * 0.35
        ctx.arc(pt.x * w, pt.y * h, pt.s * 3, 0, Math.PI * 2)
        ctx.fill()
      }
      ctx.globalAlpha = 1
      particleRafRef.current = requestAnimationFrame(loop)
    }
    particleRafRef.current = requestAnimationFrame(loop)

    return () => {
      active = false
      window.removeEventListener('resize', resize)
      if (particleRafRef.current) cancelAnimationFrame(particleRafRef.current)
    }
  }, [isOpen, reduceMotion])

  if (!isOpen || !card) return null

  const phase = getPhase(progress)
  const r = 52
  const c = 2 * Math.PI * r
  const offset = c * (1 - progress / 100)
  const imgSrc = imgError ? null : imageUrl || card.frontImageUrl || '/placeholder-card.png'

  return (
    <div
      className="fixed inset-0 z-[200] flex animate-card-journey-fade-in flex-col items-center justify-center bg-[#0A090D]/95 p-4 font-m1-ui backdrop-blur-md"
      role="dialog"
      aria-modal="true"
      aria-labelledby="card-scanner-title"
    >
      <canvas
        ref={canvasRef}
        className="pointer-events-none absolute inset-0 h-full w-full opacity-60"
      />

      <button
        type="button"
        onClick={onCancel}
        className="absolute right-[var(--gutter)] top-4 z-10 inline-flex min-h-[44px] items-center border border-white/30 px-5 text-[14px] tracking-[0.04em] text-m1-ink transition-colors hover:border-m1-violet hover:text-m1-violet"
      >
        Close
      </button>

      <div id="card-scanner-title" className="relative z-[1] mb-8">
        <img
          src="/brand/m1nt-logo.png"
          alt="M1NT"
          className="mx-auto block h-6 w-auto select-none"
          draggable={false}
        />
      </div>

      <div className="relative z-[1] flex flex-col items-center">
        <div className="card-journey-float-perspective relative mb-8 h-56 w-40 sm:h-64 sm:w-48">
          {imgSrc ? (
            <img
              src={imgSrc}
              alt=""
              className="card-journey-float h-full w-full border border-m1-line object-cover"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="card-journey-float flex h-full w-full items-center justify-center border border-m1-line bg-m1-surface">
              <i className="ri-image-line text-4xl text-m1-muted" aria-hidden="true" />
            </div>
          )}
        </div>

        <div className="relative mb-6 h-32 w-32">
          <svg className="h-full w-full -rotate-90" viewBox="0 0 120 120">
            <circle
              cx="60"
              cy="60"
              r={r}
              fill="none"
              stroke="rgba(239,235,228,0.14)"
              strokeWidth="2"
            />
            <circle
              cx="60"
              cy="60"
              r={r}
              fill="none"
              stroke="#B49CF5"
              strokeWidth="2"
              strokeLinecap="butt"
              strokeDasharray={c}
              strokeDashoffset={offset}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center font-m1-display text-[28px] text-m1-ink">
            {Math.round(progress)}%
          </div>
        </div>

        <div className="max-w-md px-2 text-center">
          <p className="mb-1 font-m1-mono text-[12px] uppercase tracking-[0.18em] text-m1-violet">
            {phase.title}
          </p>
          <p className="text-[15px] font-light text-m1-muted">{phase.sub}</p>
        </div>

        {/* Card name uses the sans font again (client request).
            To restore Bodoni, use: mt-8 px-4 text-center font-m1-display text-[clamp(24px,3vw,32px)] font-normal leading-tight text-m1-ink */}
        <p className="mt-8 px-4 text-center font-sans text-lg font-semibold leading-tight tracking-tight text-m1-ink sm:text-xl">
          {getOwnerCardDisplayName(card)}
        </p>
      </div>
    </div>
  )
}
